import { NextPage } from 'next';
import { redirect, RedirectType } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { css } from '@bts-web/utils-style-engine';
import { getAppConfig } from '@bts-web/core-app-config';
import { UserTypes } from '../userinfo/types';
import { Status } from '../components';
import { InfoLayout } from '../layout/InfoLayout/InfoLayout';
import { PoweredByTextWithLogo } from '../../trade/Reusable/PoweredBy/PoweredBy';
import { WelcomeContinueButton } from './WelcomeContinueButton';
import { getServerUserInfo } from '../userinfo/InitialUserInfoFetcherAndParser/utils/getServerUserInfo';
import { initServerTranslation, Language } from '@bts-web/utils-lokalise';
import { userDidNotProvideTaxDetails } from '../utils/userAccess/taxDetailsUtils';

export const WelcomeIndexPage: NextPage = async () => {
  const {
    welcomeContinueButtonVisual,
    welcomeScreenBadgesVisual,
    welcomeScreenInfoLayoutVariant,
    infoLayoutTextVariant,
  } = getAppConfig();

  const userData = await getServerUserInfo();

  const { providerActivated, onboarded, userType, language, isIslamicUser } =
    userData;

  const { t } = await initServerTranslation(language as Language);

  const poweredByText = t('powered_by_@tenant');

  if (userType === UserTypes.USER) {
    if (!providerActivated && onboarded) {
      redirect(NEXT_ROUTES.ERROR_GENERIC, RedirectType.replace);
    }

    if (providerActivated) {
      if (userDidNotProvideTaxDetails(userData)) {
        redirect(NEXT_ROUTES.TAX_DETAILS, RedirectType.replace);
      } else if (userData.compliance?.clientCategorization === 'PENDING') {
        redirect(NEXT_ROUTES.CLIENT_CATEGORIZATION);
      }

      if (onboarded) {
        redirect(NEXT_ROUTES.PORTFOLIO, RedirectType.replace);
      }
    }
  }

  if (userType === UserTypes.BUSINESS_USER) {
    if (onboarded) {
      redirect(NEXT_ROUTES.PORTFOLIO, RedirectType.replace);
    } else {
      redirect(NEXT_ROUTES.TERMS_AND_CONDITIONS, RedirectType.replace);
    }
  }

  return (
    <InfoLayout
      mainHeadingFirstLine={t('welcome_screen_title_line_1_@tenant')}
      mainHeadingSecondLine={t('welcome_screen_title_line_2')}
      description={t('welcome_screen_subtitle_@tenant')}
      variant={welcomeScreenInfoLayoutVariant}
      showLanguageSwitcher
      isIslamicUser={isIslamicUser}
    >
      <div
        className={css({
          display: 'flex',
          gap: 'extra_small_2',
        })}
      >
        {(
          [
            'welcome_screen_label_1',
            'welcome_screen_label_2',
            'welcome_screen_label_3',
          ] as const
        ).map((label) => (
          <Status
            key={label}
            visual={welcomeScreenBadgesVisual}
            text={t(label)}
          />
        ))}
      </div>

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'large',
          mt: 'auto',
          pb: 'medium',
        })}
        data-testid="welcome-onboarding-button-container"
      >
        <PoweredByTextWithLogo
          inverted={infoLayoutTextVariant === 'inverted'}
          noMargins
          value={poweredByText}
        />

        <WelcomeContinueButton
          visual={welcomeContinueButtonVisual}
          label={t('continue')}
        />
      </div>
    </InfoLayout>
  );
};
